import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import { Home, ChevronRight, RefreshCw, Eye, ToggleLeft, ToggleRight, Trash2, X, Icon } from 'lucide-react';

const ContributionsPage = () => {
  const { user, isReadOnly } = useAuth();
  const [contributions, setContributions] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState(null);

  // Debug - vérifier l'utilisateur connecté
  useEffect(() => {
    console.log('ContributionsPage - Utilisateur connecté:', user);
  }, [user]);
  
  // Filtres
  const [filters, setFilters] = useState({
    year: '',
    month: '',
    status: 'all',
    search: '',
    role: 'all'
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Modal de paiement
  const [paymentData, setPaymentData] = useState({
    user_id: '',
    amount: '',
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    payment_method: '',
    notes: ''
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF'
    }).format(amount || 0);
  };

  const loadContributions = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Chargement des cotisations avec filtres:', filters);
      
      // Préparer les paramètres pour l'API
      const params = {};
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'all') {
          params[key] = filters[key];
        }
      });

      console.log('Paramètres envoyés:', params);

      // Utiliser l'endpoint correct
      const response = await apiService.get('/contributions/admin', { params });
      console.log('Réponse API:', response);
      
      if (response && response.success) {
        const contributionsData = response.data || [];
        setContributions(contributionsData);
        console.log(`✅ ${contributionsData.length} cotisations chargées`);
      } else {
        console.warn('⚠️ Réponse API sans succès:', response);
        setContributions([]);
      }
    } catch (error) {
      console.error('❌ Erreur chargement cotisations:', error);
      
      // Gestion détaillée des erreurs
      if (error.response) {
        console.error('Erreur réponse:', error.response.status, error.response.data);
      }
      
      setContributions([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadMembers = useCallback(async () => {
    try {
      console.log('Chargement des membres pour admin...');
      
      // Utiliser l'endpoint admin pour récupérer tous les membres
      const response = await apiService.get('/contributions/members');
      console.log('Réponse API membres:', response);
      
      if (response && (response.success || response.data || response.members)) {
        // Adapter selon le format de réponse
        const membersData = response.data || response.members || response;
        const membersList = Array.isArray(membersData) ? membersData : [];
        setMembers(membersList);
        console.log(`✅ ${membersList.length} membres chargés`);
      } else {
        console.warn('⚠️ Réponse membres sans données:', response);
        setMembers([]);
      }
    } catch (error) {
      console.error('❌ Erreur chargement membres:', error);
      
      // Gestion détaillée des erreurs
      if (error.response) {
        console.error('Erreur réponse membres:', error.response.status, error.response.data);
      }
      
      setMembers([]);
    }
  }, []);

  useEffect(() => {
    loadContributions();
    loadMembers();
  }, [loadContributions, loadMembers]);

  // Debug function - accessible in browser console
  useEffect(() => {
    window.debugCotisations = {
      loadContributions,
      loadMembers,
      contributions,
      members,
      user,
      filters
    };
  }, [loadContributions, loadMembers, contributions, members, user, filters]);

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Fonction de rafraîchissement
  const [refreshing, setRefreshing] = useState(false);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([loadContributions(), loadMembers()]);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      // Utiliser apiService au lieu de fetch direct
      await apiService.recordPayment(paymentData);
      
      alert('✅ Paiement enregistré avec succès !');
      setShowPaymentModal(false);
      setPaymentData({
          user_id: '',
          amount: '',
          period_month: new Date().getMonth() + 1,
          period_year: new Date().getFullYear(),
          payment_method: '',
          notes: ''
        });
        loadContributions();
    } catch (error) {
      console.error('Erreur enregistrement paiement:', error);
      alert('❌ Erreur lors de l\'enregistrement: ' + (error.message || 'Erreur inconnue'));
    }
  };

  // Fonction pour voir les détails d'une cotisation
  const handleViewDetails = (contribution) => {
    console.log('🔍 Contribution sélectionnée:', contribution);
    console.log('👤 Données utilisateur:', contribution.user);
    console.log('📧 Email:', contribution.user?.email);
    console.log('📞 Téléphone:', contribution.user?.phone);
    console.log('👑 Rôle:', contribution.user?.role);
    setSelectedContribution(contribution);
    setShowDetailsModal(true);
  };

  // Fonction pour changer le statut d'une cotisation
  const handleToggleStatus = async (contribution) => {
    try {
      console.log('🔄 Changement de statut pour:', contribution);
      const newStatus = contribution.status === 'paid' ? 'pending' : 'paid';
      console.log('📊 Nouveau statut:', newStatus);
      
      const response = await apiService.updateContributionStatus(contribution.id, newStatus);
      console.log('✅ Réponse API:', response);
      
      loadContributions();
    } catch (error) {
      console.error('❌ Erreur changement statut:', error);
      console.error('📋 Détails de l\'erreur:', error.response?.data || error.message);
      alert('❌ Erreur lors du changement de statut: ' + (error.response?.data?.message || error.message));
    }
  };

  // Fonction pour supprimer une cotisation
  const handleDeleteContribution = (contribution) => {
    setSelectedContribution(contribution);
    setShowDeleteModal(true);
  };

  // Confirmation de suppression
  // Fonction d'export CSV
  const handleExport = () => {
    try {
      // Préparer les données pour l'export
      const csvData = filteredContributions.map(contribution => ({
        'Nom du membre': contribution.user_name || '',
        'Email': contribution.user?.email || '',
        'Rôle': contribution.user?.role || '',
        'Période': `${getMonthName(contribution.period_month)} ${contribution.period_year}`,
        'Montant': contribution.amount || 0,
        'Statut': contribution.status || '',
        'Date de paiement': contribution.payment_date ? new Date(contribution.payment_date).toLocaleDateString('fr-FR') : '',
        'Méthode de paiement': contribution.payment_method || '',
        'Notes': contribution.notes || '',
        'Date de création': contribution.created_at ? new Date(contribution.created_at).toLocaleDateString('fr-FR') : ''
      }));

      // Créer le contenu CSV
      const headers = Object.keys(csvData[0]);
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => {
          const value = row[header];
          // Échapper les guillemets et entourer de guillemets si nécessaire
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(','))
      ].join('\n');

      // Créer le fichier et déclencher le téléchargement
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Nom du fichier avec date/heure
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 19).replace(/[T:]/g, '-');
      link.setAttribute('download', `cotisations-fsf-${dateStr}.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log(`✅ Export réussi: ${csvData.length} cotisations exportées`);
    } catch (error) {
      console.error('❌ Erreur lors de l\'export:', error);
      alert('❌ Erreur lors de l\'export. Veuillez réessayer.');
    }
  };

  const confirmDelete = async () => {
    try {
      await apiService.deleteContribution(selectedContribution.id);
      setShowDeleteModal(false);
      setSelectedContribution(null);
      loadContributions();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('❌ Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'paid': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'overdue': 'bg-red-100 text-red-800'
    };
    const labels = {
      'paid': 'Payé',
      'pending': 'En attente',
      'overdue': 'En retard'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getMonthName = (month) => {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return months[month - 1] || month;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des cotisations...</p>
          {/* <p className="text-sm text-gray-500 mt-2">Debug: Utilisateur = {user ? `${user.first_name} (${user.role})` : 'Non connecté'}</p> */}
        </div>
      </div>
    );
  }

  // Debug: Affichage d'informations supplémentaires
  console.log('Render ContributionsPage:', {
    user,
    contributionsCount: contributions.length,
    membersCount: members.length,
    loading,
    filters
  });

  // Debug pour le filtrage
  console.log('🔍 Filtrage:', {
    totalContributions: contributions.length,
    filters
  });

  // Filtrage côté client pour une meilleure responsivité
  const filteredContributions = contributions.filter(contribution => {
    // Filtre par année
    if (filters.year && contribution.period_year.toString() !== filters.year) {
      return false;
    }
    
    // Filtre par mois
    if (filters.month && contribution.period_month.toString() !== filters.month) {
      return false;
    }
    
    // Filtre par statut
    if (filters.status !== 'all' && contribution.status !== filters.status) {
      return false;
    }
    
    // Filtre par rôle
    if (filters.role !== 'all' && contribution.user?.role !== filters.role) {
      return false;
    }
    
    // Filtre par recherche (nom, email)
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const userName = (contribution.user_name || '').toLowerCase();
      const userEmail = (contribution.user?.email || '').toLowerCase();
      
      if (!userName.includes(searchTerm) && !userEmail.includes(searchTerm)) {
        return false;
      }
    }
    
    return true;
  }).sort((a, b) => {
    // Tri par date de création (created_at) du plus récent au plus ancien
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  // Logique de pagination
  const totalItems = filteredContributions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContributions = filteredContributions.slice(startIndex, endIndex);

  // Fonction pour changer de page
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 space-y-3 sm:space-y-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <Link to="/" className="flex items-center text-gray-500 hover:text-fsf-primary transition-colors">
                  <Home className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="font-medium text-sm sm:text-base">Retour au site</span>
                </Link>
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                <Link to="/dashboard" className="text-gray-500 hover:text-fsf-primary transition-colors text-sm sm:text-base">
                  Dashboard
                </Link>
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
              </div>
              <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                <span className="sm:hidden">Cotisations</span>
                <span className="hidden sm:inline">Administration des Cotisations</span>
              </h1>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto justify-between sm:justify-end">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white/70 border border-gray-200 rounded-xl hover:bg-white/90 transition-all duration-300 shadow-sm"
              >
                <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Actualiser</span>
                <span className="sm:hidden">↻</span>
              </button>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                  {user?.first_name || user?.name}
                </span>
                <span className="text-xs px-2 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full">
                  {user?.role}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Total Cotisations */}
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-5">
                <p className="text-sm font-medium text-gray-500">Total Cotisations</p>
                <p className="text-2xl font-semibold text-gray-900">{contributions.length}</p>
              </div>
            </div>
          </div>

          {/* Calendarotisations Payées */}
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5">
                <p className="text-sm font-medium text-gray-500">Payées</p>
                <p className="text-2xl font-semibold text-green-600">{contributions.filter(c => c.status === 'paid').length}</p>
              </div>
            </div>
          </div>

          {/* Cotisations en Attente */}
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5">
                <p className="text-sm font-medium text-gray-500">En Attente</p>
                <p className="text-2xl font-semibold text-yellow-600">{contributions.filter(c => c.status === 'pending').length}</p>
              </div>
            </div>
          </div>

          {/* Membres Disponibles */}
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-5">
                <p className="text-sm font-medium text-gray-500">Membres</p>
                <p className="text-2xl font-semibold text-purple-600">{members.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section Filtres et Tableau */}
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
          {/* En-tête de section avec bouton d'ajout */}
          <div className="px-6 py-4 border-b border-white/20 bg-gradient-to-r from-purple-500/10 to-blue-500/10">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                🏛️ Gestion des Cotisations
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={handleExport}
                  disabled={filteredContributions.length === 0}
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Exporter ({filteredContributions.length})
                </button>
                {/* <button
                  onClick={() => setShowPaymentModal(true)}
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Enregistrer un paiement
                </button> */}
              </div>
            </div>
          </div>

          {/* Filtres intégrés dans la carte */}
          <div className="px-4 sm:px-6 py-4 bg-gray-50/50">
            <div className="flex flex-col space-y-4">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                </svg>
                <span className="font-semibold text-gray-700 text-sm sm:text-base">Filtres</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Année</label>
                  <select
                    value={filters.year}
                    onChange={(e) => handleFilterChange('year', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/70"
                  >
                    <option value="">Tous</option>
                    <option value={2030}>2030</option>
                    <option value={2029}>2029</option>
                    <option value={2028}>2028</option>
                    <option value={2027}>2027</option>
                    <option value={2026}>2026</option>
                    <option value={2025}>2025</option>
                    <option value={2024}>2024</option>
                    <option value={2023}>2023</option>
                    <option value={2022}>2022</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Mois</label>
                  <select
                    value={filters.month}
                    onChange={(e) => handleFilterChange('month', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/70"
                  >
                    <option value="">Tous</option>
                    {Array.from({length: 12}, (_, i) => (
                      <option key={i+1} value={i+1}>{getMonthName(i+1)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/70"
                  >
                    <option value="all">Tous</option>
                    <option value="paid">Payés</option>
                    <option value="pending">En attente</option>
                    <option value="overdue">En retard</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Rôle</label>
                  <select
                    value={filters.role}
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/70"
                  >
                    <option value="all">Tous</option>
                    <option value="admin">Admin</option>
                    <option value="apd">APD</option>
                    <option value="apz">APZ</option>
                    <option value="tgd">TGD</option>
                    <option value="tgz">TGZ</option>
                    <option value="mcd">MCD</option>
                    <option value="mcz">MCZ</option>
                    <option value="moderateur">Modérateur</option>
                    <option value="membre">Membre</option>
                  </select>
                </div>

                <div className="col-span-2 sm:col-span-1 lg:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Recherche</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder="Nom, email..."
                      className="w-full border border-gray-200 rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 pl-8 sm:pl-10 text-xs sm:text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/70"
                    />
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 absolute left-2 sm:left-3 top-2 sm:top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Boutons d'action pour les filtres */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-4">
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200 disabled:opacity-50"
                    title="Actualiser les données"
                  >
                    <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">{refreshing ? 'Actualisation...' : 'Actualiser'}</span>
                    <span className="sm:hidden">↻</span>
                  </button>
                  <button
                    onClick={() => setFilters({
                      year: '',
                      month: '',
                      status: 'all',
                      search: '',
                      role: 'all'
                    })}
                    className="flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                    title="Réinitialiser les filtres"
                  >
                    <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    <span className="hidden sm:inline">Réinitialiser</span>
                    <span className="sm:hidden">Reset</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
             
            </div>

           
          </div>

          {/* Tableau des cotisations */}
          <div className="overflow-hidden p-4 sm:p-8">
            <div className="px-4 sm:px-6 py-4 bg-white border-b border-gray-200">
              <h3 className="text-base sm:text-lg text-justify font-medium bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                <span className="hidden sm:inline">📊 Liste des cotisations ({contributions.length})</span>
                <span className="sm:hidden">📊 Cotisations ({contributions.length})</span>
              </h3>
            </div>
            
            {/* Vue desktop - tableau */}
            <div className="hidden md:block overflow-x-auto">
              <table className="text-justify min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Membre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date paiement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Méthode
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedContributions.map((contribution, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {/* Afficher icone money */}
                      <span className="mr-2 rounded-full bg-yellow-300 text-yellow-800 p-2">💰</span>
                   
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {contribution.user_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {contribution.user?.email || 'Email non disponible'} • {contribution.user?.role || 'Rôle non défini'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getMonthName(contribution.period_month)} {contribution.period_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {formatCurrency(contribution.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(contribution.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {contribution.created_at ? 
                        new Date(contribution.created_at).toLocaleDateString('fr-FR') : 
                        '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {contribution.payment_method || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        {/* Bouton voir détails */}
                        <button
                          onClick={() => handleViewDetails(contribution)}
                          className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        
                        {/* Bouton changer statut */}
                        {/* <button
                          onClick={() => handleToggleStatus(contribution)}
                          className={`transition-colors duration-200 ${
                            contribution.status === 'paid' 
                              ? 'text-green-600 hover:text-green-800' 
                              : 'text-gray-400 hover:text-gray-600'
                          }`}
                          title={contribution.status === 'paid' ? 'Marquer comme impayé' : 'Marquer comme payé'}
                        >
                          {contribution.status === 'paid' ? (
                            <ToggleRight className="h-4 w-4" />
                          ) : (
                            <ToggleLeft className="h-4 w-4" />
                          )}
                        </button> */}
                        
                        {/* Bouton supprimer */}
                        {/* <button
                          onClick={() => handleDeleteContribution(contribution)}
                          className="text-red-600 hover:text-red-800 transition-colors duration-200"
                          title="Supprimer la cotisation"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Vue mobile - cartes */}
            <div className="md:hidden space-y-4 p-4">
              {paginatedContributions.map((contribution, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  {/* Header de la carte */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="h-10 w-10 rounded-full bg-purple-500 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                        {contribution.user_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {contribution.user_name}
                        </h3>
                        <p className="text-xs text-gray-500 truncate">
                          {contribution.user?.email || 'Email non disponible'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button
                        onClick={() => handleViewDetails(contribution)}
                        className="text-blue-600 hover:text-blue-800 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {!isReadOnly && (
                      <button
                        onClick={() => handleToggleStatus(contribution)}
                        className={`p-1.5 rounded-md transition-colors ${
                          contribution.status === 'paid' 
                            ? 'text-green-600 hover:text-green-800 hover:bg-green-50' 
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                        }`}
                        title={contribution.status === 'paid' ? 'Marquer comme impayé' : 'Marquer comme payé'}
                      >
                        {contribution.status === 'paid' ? (
                          <ToggleRight className="h-4 w-4" />
                        ) : (
                          <ToggleLeft className="h-4 w-4" />
                        )}
                      </button>
                      )}
                      {!isReadOnly && (
                      <button
                        onClick={() => handleDeleteContribution(contribution)}
                        className="text-red-600 hover:text-red-800 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                        title="Supprimer la cotisation"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      )}
                    </div>
                  </div>

                  {/* Contenu de la carte */}
                  <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                      <span className="text-gray-500 text-xs">Période:</span>
                      <div className="mt-1 font-medium text-gray-900">
                        {getMonthName(contribution.period_month)} {contribution.period_year}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs">Montant:</span>
                      <div className="mt-1 font-bold text-gray-900">
                        {formatCurrency(contribution.amount)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs">Statut:</span>
                      <div className="mt-1">
                        {getStatusBadge(contribution.status)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500 text-xs">Méthode:</span>
                      <div className="mt-1 text-gray-900">
                        {contribution.payment_method || '-'}
                      </div>
                    </div>
                  </div>

                  {/* Footer de la carte */}
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        Rôle: {contribution.user?.role || 'Non défini'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {contribution.created_at ? 
                          `Créé le ${new Date(contribution.created_at).toLocaleDateString('fr-FR')}` : 
                          'Date inconnue'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {filteredContributions.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Aucune cotisation trouvée avec ces filtres
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredContributions.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
              
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 text-justify">
                    Affichage de <span className="font-medium">{startIndex + 1}</span> à{' '}
                    <span className="font-medium">{Math.min(endIndex, totalItems)}</span> sur{' '}
                    <span className="font-medium">{totalItems}</span> résultats
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    {/* Bouton précédent */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Précédent</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    {/* Numéros de page */}
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    {/* Bouton suivant */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Suivant</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

      {/* Modal de paiement */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">💳 Enregistrer un paiement</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handlePaymentSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Membre *</label>
                  <select
                    value={paymentData.user_id}
                    onChange={(e) => {
                      const selectedMember = members.find(m => m.id === parseInt(e.target.value));
                      setPaymentData(prev => ({
                        ...prev,
                        user_id: e.target.value,
                        amount: selectedMember ? selectedMember.contribution_monthly : ''
                      }));
                    }}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner un membre</option>
                    {members.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.name} ({member.role}) - {formatCurrency(member.contribution_monthly)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mois *</label>
                    <select
                      value={paymentData.period_month}
                      onChange={(e) => setPaymentData(prev => ({...prev, period_month: e.target.value}))}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {Array.from({length: 12}, (_, i) => (
                        <option key={i+1} value={i+1}>{getMonthName(i+1)}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Année *</label>
                    <select
                      value={paymentData.period_year}
                      onChange={(e) => setPaymentData(prev => ({...prev, period_year: e.target.value}))}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={2025}>2025</option>
                      <option value={2024}>2024</option>
                      <option value={2023}>2023</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Montant (XOF) *
                    {paymentData.user_id && (
                      <span className="text-gray-500">
                        - Min: {formatCurrency(members.find(m => m.id === parseInt(paymentData.user_id))?.contribution_monthly || 0)}
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData(prev => ({...prev, amount: e.target.value}))}
                    required
                    min={members.find(m => m.id === parseInt(paymentData.user_id))?.contribution_monthly || 0}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Méthode de paiement</label>
                  <select
                    value={paymentData.payment_method}
                    onChange={(e) => setPaymentData(prev => ({...prev, payment_method: e.target.value}))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner</option>
                    <option value="cash">Espèces</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="bank_transfer">Virement bancaire</option>
                    <option value="cheque">Chèque</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData(prev => ({...prev, notes: e.target.value}))}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Notes additionnelles..."
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  💾 Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de détails */}
      {showDetailsModal && selectedContribution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">👁️ Détails de la cotisation</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Informations du membre */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  👤 Informations du membre
                </h4>
                <div className="space-y-2 text-justify">
                  <p><span className="font-medium text-gray-700">ID :</span> #{selectedContribution.user?.id}</p>
                  <p><span className="font-medium text-gray-700">Nom complet :</span> {selectedContribution.user?.name}</p>
                  <p><span className="font-medium text-gray-700">Email :</span> 
                    <a href={`mailto:${selectedContribution.user?.email}`} className="text-blue-600 hover:text-blue-800 ml-1">
                      {selectedContribution.user?.email}
                    </a>
                  </p>
                  <p><span className="font-medium text-gray-700">Téléphone :</span> 
                    {selectedContribution.user?.phone ? (
                      <a href={`tel:${selectedContribution.user?.phone}`} className="text-blue-600 hover:text-blue-800 ml-1">
                        {selectedContribution.user?.phone}
                      </a>
                    ) : (
                      <span className="text-gray-500 ml-1">Non renseigné</span>
                    )}
                  </p>
                  <p><span className="font-medium text-gray-700">Rôle :</span> 
                    <span className={`ml-1 px-2 py-1 text-xs rounded-full ${
                      selectedContribution.user?.role === 'admin' ? 'bg-red-100 text-red-800' :
                      selectedContribution.user?.role === 'moderator' ? 'bg-orange-100 text-orange-800' :
                      selectedContribution.user?.role === 'member' ? 'bg-blue-100 text-blue-800' :
                      selectedContribution.user?.role === 'APD' ? 'bg-purple-100 text-purple-800' :
                      selectedContribution.user?.role === 'APZ' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedContribution.user?.role || 'Non défini'}
                    </span>
                  </p>
                </div>
              </div>

              {/* Informations de la cotisation */}
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  💳 Détails de la cotisation
                </h4>
                <div className="space-y-2 text-justify">
                  <p><span className="font-medium text-gray-700">Période :</span> {getMonthName(selectedContribution.period_month)} {selectedContribution.period_year}</p>
                  <p><span className="font-medium text-gray-700">Montant :</span> {formatCurrency(selectedContribution.amount)}</p>
                  <p><span className="font-medium text-gray-700">Statut :</span> {getStatusBadge(selectedContribution.status)}</p>
                  <p><span className="font-medium text-gray-700">Date de paiement :</span> {selectedContribution.payment_date ? new Date(selectedContribution.payment_date).toLocaleDateString('fr-FR') : 'Non payé'}</p>
                  <p><span className="font-medium text-gray-700">Méthode :</span> {selectedContribution.payment_method || 'Non spécifiée'}</p>
                  <p><span className="font-medium text-gray-700">Date de création :</span> {new Date(selectedContribution.created_at).toLocaleDateString('fr-FR')}</p>
                  {selectedContribution.notes && (
                    <p><span className="font-medium text-gray-700">Notes :</span> {selectedContribution.notes}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && selectedContribution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">🗑️ Confirmer la suppression</h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2 text-justify">
                Êtes-vous sûr de vouloir supprimer cette cotisation ?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <span className="font-medium">Membre :</span> {selectedContribution.user?.name}<br />
                  <span className="font-medium">Période :</span> {getMonthName(selectedContribution.period_month)} {selectedContribution.period_year}<br />
                  <span className="font-medium">Montant :</span> {formatCurrency(selectedContribution.amount)}
                </p>
              </div>
              <p className="text-red-600 text-sm mt-2 font-medium">
                ⚠️ Cette action est irréversible !
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                🗑️ Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContributionsPage;